// Imports (unchanged)
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import FeatureReductionCluster from "@arcgis/core/layers/support/FeatureReductionCluster";
import * as promiseUtils from "@arcgis/core/core/promiseUtils";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { fromJSON } from "@arcgis/core/renderers/support/jsonUtils.js";
import { LOG_MODULES, ALL_RECORDS } from "@config/config";
import { log } from '@libs/utils/helper.js';
import { niogemsGisWellApi } from '@/libs/utils/apiEndpointList';

// 🆕 Shared cluster config factory — replaces the local createClusterConfig()
// that used to live in this file. See clusterConfig.js for the "simple",
// "count", and "pie" variants.
import { createClusterConfig } from './clusterConfig';

// NOTE: We now rely on config values. No local COUNT_OFF/COUNT_ON/USE_CUST_RENDER flags.

// ── MAIN EXPORT — stays SYNC so layer is returned immediately ─────────────────
export default function buildWellLayerAdaptiveCluster(view, config) {
  const {
    cluster = {},
    renderer = {},
    ...layerProps
  } = config || {};

  const clusterType = cluster.type ?? "simple";
  const isSmartPie = clusterType === "smart-pie";

  // ----------------------------------------------------------
  // Synchronous cluster types
  //
  // These can be created before FeatureLayer exists.
  // ----------------------------------------------------------

  const rawConfig = createClusterConfig({ type: clusterType, ...cluster });
  const { type: _appClusterType, ...apiReadyConfig } = rawConfig; // drop before passing to FeatureReductionCluster
  const clusterConfig = cluster.enabled && !isSmartPie ? new FeatureReductionCluster(apiReadyConfig) : null;
  
  // Initialize layer with featureReduction included in the constructor object
  const layer = new FeatureLayer({ ...layerProps,  featureReduction: clusterConfig });

   // ----------------------------------------------------------
  // Smart pie must happen AFTER layer exists.
  // ----------------------------------------------------------

  if (cluster.enabled && isSmartPie && view ) {
    initializeSmartPie( layer,view, cluster, ).catch((err) => {
      log(LOG_MODULES.ERROR, "[Wells] Smart pie initialization failed", err, ); });
  }

  // Wire adaptive behavior
  if (clusterConfig && view) {
    setupAdaptiveClustering(view, layer, clusterConfig, cluster)
      .catch(err => log(LOG_MODULES.ERROR, "[Wells] Adaptive clustering wiring failed", err));
  }

  return layer;
}

// ── 2. CLUSTER CONFIG ──────────────────────────────────────────
// (moved to clusterConfig.js — see createClusterConfig({ type: 'simple', ... }) above)

// ── 3. ADAPTIVE CLUSTERING — uses config.cluster.countOff/countOn ─────────────
async function setupAdaptiveClustering(view, layer, clusterConfig, cluster) {
  log(LOG_MODULES.CLUSTER, "[Wells] Initializing adaptive clustering logic...");

  const layerView = await view.whenLayerView(layer);
  
  const COUNT_OFF = cluster?.countOff ?? 1200;
  const COUNT_ON  = cluster?.countOn  ?? 1500;

  const evaluate = async () => {
    // If the view isn't ready or extent is missing, skip
    if (!view?.extent || !view?.ready) return;
    
    const isClusterOn = !!layer.featureReduction;

    try {
      const visibleCount = await layer.queryFeatureCount({
        where: ALL_RECORDS,
        geometry: view.extent,
        spatialRelationship: "intersects",
        outSpatialReference: view.spatialReference
      });
      
      log(LOG_MODULES.CLUSTER, `[Adaptive] Density (${visibleCount}) at Zoom (${view.zoom})`);

      // Toggle clustering based on count
      if (visibleCount <= COUNT_OFF && isClusterOn) {
        log(LOG_MODULES.CLUSTER, `[Adaptive] Turning OFF clusters (Count: ${visibleCount})`);
        layer.featureReduction = null;
      } else if (visibleCount >= COUNT_ON && !isClusterOn) {
        log(LOG_MODULES.CLUSTER, `[Adaptive] Turning ON clusters (Count: ${visibleCount})`);
        layer.featureReduction = clusterConfig;
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        log(LOG_MODULES.ERROR, "[Adaptive] queryFeatureCount error:", err);
      }
    }
  };

  // 1. Run immediately on load
  // We don't wait for 'stationary' because we might already be stationary
  await evaluate();

  // 2. Setup debounced version for movement
  const debouncedEvaluate = promiseUtils.debounce(() => {
    if (view.stationary) evaluate();
  });

  // 3. Watch for state changes
  const handles = [
    reactiveUtils.watch(() => view.stationary, (s) => s && debouncedEvaluate()),
    reactiveUtils.watch(() => view.zoom, () => debouncedEvaluate())
  ];

  log(LOG_MODULES.CLUSTER, "[Wells] Adaptive watchers active.");

  layer.__disposeWatcher = () => {
    log(LOG_MODULES.CLUSTER, "[Wells] Cleaning up watchers.");
    handles.forEach(h => h.remove());
  };
}

// ── 1. APPLY RENDERER (async, non-blocking) ───────────────────────────────────
async function applyServerRenderer(layer, serviceUrl, useCustomFallback = false) {
  try {
    const response  = await niogemsGisWellApi.get(serviceUrl, { params: { f: 'json' } });
    const layerInfo = response?.data;

    if (!layerInfo?.drawingInfo?.renderer) {
      log(LOG_MODULES.CLUSTER, "[Wells] No drawingInfo — using fallback");
      if (useCustomFallback) {
        layer.renderer = buildWellRenderer();
      }
      return;
    }

    const rawRenderer = layerInfo.drawingInfo.renderer;

    // Normalize to REST JSON
    const restJson = (typeof rawRenderer.toJSON === "function")
      ? rawRenderer.toJSON()
      : JSON.parse(JSON.stringify(rawRenderer));

    // Sanitize plain REST JSON to avoid oversized markers/outlines
    const sanitized = sanitizeRenderer(restJson);

    // Convert to JS API renderer instance
    const finalRenderer = fromJSON(sanitized);

    if (!finalRenderer) {
      log(LOG_MODULES.ERROR, "[Wells] fromJSON returned null — check sanitized JSON");
      console.error("[Wells] sanitized renderer JSON:", sanitized);
      return;
    }

    await layer.when();
    layer.renderer = finalRenderer;
    layer.refresh();

    log(LOG_MODULES.CLUSTER, "[Wells] Server renderer applied successfully");
  } catch (err) {
    log(LOG_MODULES.ERROR, "[Wells] applyServerRenderer failed:", err);
    if (useCustomFallback) {
      layer.renderer = buildWellRenderer();
    }
  }
}

function sanitizeRenderer(restJson) {
  if (!restJson) return null;
  const r = JSON.parse(JSON.stringify(restJson));

  const fixSymbol = (sym) => {
    if (!sym) return;
    const isSimpleMarker = sym.type === "esriSMS" || sym.type === "simple-marker";
    //console.error("sanitizeRendere1r fixSymbol",sym);
    if (!isSimpleMarker) return;
    

    // Prevent giant markers/outline from the service
    sym.size = sym.size > 10 ? 8 : sym.size;
    sym.width = sym.width > 6 ? 4 : sym.width;

    if (!sym.outline) {
      sym.outline = { width: 0.8, color: [255, 255, 255, 200] };
    } else if (sym.outline.width > 4) {
      sym.outline.width = 0.8;
      sym.outline.color = [255, 255, 255, 200];
    }
  };

  r.uniqueValueInfos?.forEach(info =>{
    //if(info.symbol.type === "esriSMS" || info.symbol.type === "simple-marker")
    //console.error("999   sanitizeRendere1r fixSymbol",info.value);
    fixSymbol(info.symbol);
    }
  );
  r.uniqueValueGroups?.forEach(group => {
    group.classes?.forEach(cls => {
      cls.symbols?.forEach(s => fixSymbol(s.symbol ?? s));
      if (cls.symbol) fixSymbol(cls.symbol);
    });
  });

  return r;
}

// ── 4. CUSTOM RENDERER (fallback) ─────────────────────────────────────────────
function buildWellRenderer() {
  
  return {
    type:  "unique-value",
    field: "CURRENT_STATUS",
    defaultSymbol: {
      type:    "simple-marker",
      style:   "circle",
      color:   [180, 180, 180, 200],
      size:    6,
      outline: { color: [100, 100, 100, 255], width: 0.5 }
    },
    uniqueValueInfos: statusSymbols.map(({ value, color, size, label }) => ({
      value,
      label,
      symbol: {
        type:    "simple-marker",
        style:   "circle",
        color,
        size,
        outline: { color: [255, 255, 255, 180], width: 0.5 }
      }
    }))
  };
}


async function initializeSmartPie(
  layer,
  view,
  cluster,
) {
  // Layer must exist/load before smart mapping.
  await layer.load();

  const featureReduction =
    await createClusterConfig({
      type: "smart-pie",

      layer,
      view,

      ...cluster,
    });

  layer.featureReduction =
    new FeatureReductionCluster(
      featureReduction,
    );

  log(
    LOG_MODULES.CLUSTER,
    "[Wells] Smart pie cluster applied",
  );

  // Optional:
  // wire adaptive clustering after smart-pie exists.

  if (cluster.enabled) {
    await setupAdaptiveClustering(
      view,
      layer,
      layer.featureReduction,
      cluster,
    );
  }
}

const statusSymbols = [
    { value: "GAS",     color: [0, 255, 25, 250],    size: 8, label: "Active Gas" },
    { value: "OIL",     color: [0, 255, 25, 250],    size: 8, label: "Active Oil" },
    { value: "CBM",     color: [0, 168, 132, 255],   size: 8, label: "Active Coalbed Methane" },
    { value: "INJ",     color: [0, 112, 255, 255],   size: 8, label: "Active Injection" },
    { value: "O&I",     color: [0, 112, 255, 255],   size: 8, label: "Active Oil & Injection" },
    { value: "INA-GAS", color: [168, 168, 0, 255],   size: 7, label: "Inactive Gas" },
    { value: "INA-OIL", color: [0, 0, 0, 255],       size: 7, label: "Inactive Oil" },
    { value: "INA-CBM", color: [100, 100, 100, 255], size: 7, label: "Inactive CBM" },
    { value: "INA-CO2", color: [100, 100, 100, 255], size: 7, label: "Inactive CO2" },
    { value: "INA-G&I", color: [100, 100, 100, 255], size: 7, label: "Inactive Gas & Injection" },
    { value: "INA-INJ", color: [100, 100, 100, 255], size: 7, label: "Inactive Injection" },
    { value: "INA-O&I", color: [100, 100, 100, 255], size: 7, label: "Inactive Oil & Injection" },
    { value: "ABD-GAS", color: [255, 0, 0, 200],     size: 6, label: "Plugged Gas" },
    { value: "ABD-O&I", color: [255, 0, 0, 200],     size: 6, label: "Plugged Oil & Injection" },
    { value: "ABD-OG",  color: [255, 0, 0, 200],     size: 6, label: "Plugged Oil & Gas" },
    { value: "ABD-OIL", color: [255, 0, 0, 200],     size: 6, label: "Plugged Oil" },
    { value: "D&A",     color: [255, 0, 0, 200],     size: 6, label: "Plugged D&A" },
    { value: "LOC",     color: [0, 0, 0, 255],       size: 7, label: "Location" },
    { value: "OBS",     color: [190, 190, 190, 255], size: 6, label: "Observation" },
    { value: "OTHER",   color: [190, 190, 190, 255], size: 6, label: "Other" },
    { value: "TA",      color: [130, 130, 130, 255], size: 5, label: "Temporarily Abandoned" },
  ];